import { supabase } from './supabaseClient';

/**
 * Update an existing zone in Supabase
 * @param {string} zoneId - The zone ID to update
 * @param {Object} zoneData - The zone data to update
 * @returns {Promise<Object>}
 */
export const updateZone = async (zoneId, zoneData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('✏️ Updating zone:', zoneId, zoneData);

    // Update the zone
    const { data, error } = await supabase
      .from('zones')
      .update({
        name: zoneData.name,
        type: zoneData.type,
        custom_type: zoneData.custom_type,
        description: zoneData.description,
        color: zoneData.color,
        sensor_ids: zoneData.sensor_ids,
        gateway_ids: zoneData.gateway_ids,
        street_address: zoneData.street_address,
        city: zoneData.city,
        state: zoneData.state,
        zip_code: zoneData.zip_code,
        country: zoneData.country,
        latitude: zoneData.latitude,
        longitude: zoneData.longitude,
        address_formatted: zoneData.address_formatted,
        updated_at: new Date().toISOString(),
      })
      .eq('id', zoneId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating zone:', error);
      throw new Error(error.message || 'Failed to update zone');
    }

    // Update sensor associations
    // First, clear zone_id from all sensors previously associated with this zone
    await supabase
      .from('sensors')
      .update({ zone_id: null })
      .eq('zone_id', zoneId)
      .eq('user_id', user.id);

    // Then assign zone_id to the selected sensors
    if (zoneData.sensor_ids && zoneData.sensor_ids.length > 0) {
      await supabase
        .from('sensors')
        .update({ zone_id: zoneId })
        .in('id', zoneData.sensor_ids)
        .eq('user_id', user.id);
    }

    console.log('✅ Zone updated successfully');
    return { success: true, data };
  } catch (error) {
    console.error('❌ Error in updateZone:', error);
    throw error;
  }
};

/**
 * Delete a zone from Supabase
 * @param {string} zoneId - The zone ID to delete
 * @returns {Promise<void>}
 */
export const deleteZone = async (zoneId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('🗑️ Deleting zone:', zoneId);

    // First, unassign any sensors from this zone
    // Update sensors to remove zone_id association
    const { error: sensorsError } = await supabase
      .from('sensors')
      .update({ zone_id: null })
      .eq('zone_id', zoneId)
      .eq('user_id', user.id);

    if (sensorsError) {
      console.error('❌ Error unassigning sensors from zone:', sensorsError);
      throw new Error('Failed to unassign sensors from zone');
    }

    console.log('✅ Sensors unassigned from zone successfully');

    // Delete the zone
    const { error: zoneError } = await supabase
      .from('zones')
      .delete()
      .eq('id', zoneId)
      .eq('user_id', user.id);

    if (zoneError) {
      console.error('❌ Error deleting zone:', zoneError);
      throw new Error(zoneError.message || 'Failed to delete zone');
    }

    console.log('✅ Zone deleted successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error in deleteZone:', error);
    throw error;
  }
};

export default { updateZone, deleteZone };
